// ADR 30.9.26l §2 — what Accept and Decline do, in order.
//
// Neither adds a way to construct an SDK: Accept goes through 30.9.26i's
// `grantConsent`, which re-reads the gate after its `import()` resolves, so an
// SDK is still constructed only when the gate read at that moment grants it
// (30.7.26a, 6.9.26c). Nothing from before the answer is replayed.

import { capture } from '../telemetry/capture'
import { DEFAULT_CONSENT, writeLocalConsent } from '../telemetry/consent'
import { forgetConsent, grantConsent } from '../telemetry/consentControl'
import { CONSENT_SDKS, sdkState } from '../telemetry/lifecycle'
import { markConsentAnswered } from './consentAsked'

/**
 * `failed` — a grant the server refused (the snackbar is `error_unknown`);
 * `next_load` — recorded, but this page already tore an SDK down once
 * (`settings_privacy_next_visit`); `granted` — in force now.
 */
export type AcceptOutcome = 'granted' | 'next_load' | 'failed'

/**
 * Analytics first, then crash reports. `userId` is `null` only for a settled
 * `signedOut` session — the strip renders nothing while the session loads — so
 * a restoring learner's Accept still goes to the server first.
 *
 * A refused analytics grant writes nothing, skips crash reports and leaves the
 * strip up. A refused crash-reports grant after a good analytics one still
 * hides the strip: the record now carries the marker, and `/account` is the
 * retry.
 */
export async function acceptConsent(userId: string | null, compact: boolean): Promise<AcceptOutcome> {
  const analytics = await grantConsent('analytics', userId)
  if (analytics === 'failed') return 'failed'
  // §7 — after the attach, so PostHog is there to carry it; a no-op in a build
  // with no key, and never on a re-Accept that waits for the next load.
  if (analytics === 'granted') capture('web_consent_banner_answered', { choice: 'accept', compact })
  const crashReports = await grantConsent('crash_reports', userId)
  markConsentAnswered()
  if (crashReports === 'failed') return 'failed'
  return analytics === 'next_load' || crashReports === 'next_load' ? 'next_load' : 'granted'
}

/**
 * A choice for this browser only: no `PUT`, even signed in. The account row
 * governs the phones too, and withdrawing account-wide stays on `/account`.
 *
 * An SDK can be live here only under a grant an `/account` toggle wrote before
 * 30.9.26l shipped (a pair with no marker); `forgetConsent` tears it down
 * locally first, and drops that learner's PostHog identity for the page.
 */
export function declineConsent(): void {
  const running = CONSENT_SDKS.some((sdk) => sdkState(sdk) === 'live' || sdkState(sdk) === 'constructing')
  if (running) forgetConsent()
  writeLocalConsent({ ...DEFAULT_CONSENT, answered: true })
  markConsentAnswered()
}
